import DownloadIcon from '@mui/icons-material/Download';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Stack, SxProps, Theme, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { MediaPreviewModal } from './MediaPreviewModal';
import { RootState } from 'FRONTEND/reducers/types';
import { selectConsultationAttachmentInfo } from 'FRONTEND/selectors/consultationSelectors';


type MediaControlsProps = {
  children: React.ReactNode;
  mediaId?: string;
  sx?: SxProps<Theme>;
  mediaType?: 'photo' | 'video';
};

type AttachmentInfo = {
  id: number;
  path: string;
  fileName: string;
  fileType: string;
  fileExtension: string;
  description?: string;
  group?: string;
  category?: string;
  date?: string;
  origin?: string;
  transitionLabel?: Record<string, string>;
};

export const MediaControls = ({ children, mediaId, sx, mediaType }: MediaControlsProps) => {
  const locale = useSelector((state: RootState) => state.user.locale);
  const attachmentInfo: AttachmentInfo | undefined = useSelector((state: RootState) => selectConsultationAttachmentInfo(state, mediaId));
  const { path, fileName, description, date, origin, transitionLabel } = attachmentInfo || {};

  // Description formatting
  const translatedTransition = transitionLabel?.[locale];
  const formatedDate = date ? dayjs(date).format('DD MMM YYYY, HH:mm:ss') : ''; // eg: 27 avr. 2026, 15:22:47
  const descriptionText = [translatedTransition, origin, formatedDate].filter(Boolean).join(' / ');
  const photoNote = description;

  // Modal Management
  const [isModalOpen, setIsModalOpen] = useState(false);

  // IconStyles
  const iconButtonStyles = {
    color: (theme) => theme.palette.grey[800],
    backgroundColor: (theme) => theme.palette.grey[100],
    '&:hover': {
      backgroundColor: (theme) => theme.palette.grey[200],
    },
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {
        photoNote && (
          <Typography sx={{ paddingTop: 1 }}>
            {photoNote}
          </Typography>
        )
      }
      <Stack sx={{ position: 'absolute', left: 0, height: '45px', backgroundColor: 'rgba(0, 0, 0, 0.3)', px: 2, py: 1 }} direction='row' spacing={1} alignItems='center'>
        {fileName && (
          <Typography variant='caption' color='primary' sx={{ pr: 1 }} fontSize={18}>
            {fileName}
          </Typography>
        )}
        <IconButton size='small' color='primary' sx={iconButtonStyles} onClick={() => setIsModalOpen(true)}>
          <FullscreenExitIcon fontSize='small' />
        </IconButton>
        <IconButton size='small' color='primary' sx={iconButtonStyles} target='_blank' href={path}>
          <OpenInNewIcon fontSize='small' />
        </IconButton>
        <IconButton size='small' color='primary' sx={iconButtonStyles} download href={path}>
          <DownloadIcon fontSize='small' />
        </IconButton>
      </Stack>
      {children}
      <MediaPreviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mediaSrc={path}
        mediaType={mediaType}
        mediaTitle={fileName}
        description={descriptionText}
      />
    </Box>
  );
};
